import React, { useEffect, useState } from "react";
import PageHeader from "../../../components/common/PageHeader";
import Spinner from "../../../components/common/Spinner";
import ErrorAlert from "../../../components/common/ErrorAlert";
import EmptyState from "../../../components/common/EmptyState";
import ConfirmDialog from "../../../components/common/ConfirmDialog";
import {
  fetchComplianceRegister,
  createComplianceItem,
  updateComplianceItem,
  deleteComplianceItem,
} from "../api";
import type { ComplianceRegister } from "@akfdev-com/shared/src/models/ComplianceRegister";
import type {
  ComplianceCategory,
  ComplianceFrequency,
  ComplianceStatus,
} from "@akfdev-com/shared/src/models/enums";

const CATEGORIES: ComplianceCategory[] = [
  "insurance",
  "fire-safety",
  "electrical",
  "gas",
  "water-safety",
  "lifts",
  "building-fabric",
  "governance",
];

const FREQUENCIES: ComplianceFrequency[] = [
  "weekly",
  "monthly",
  "quarterly",
  "every-6-months",
  "annual",
  "every-2-years",
  "every-5-years",
];

const STATUSES: ComplianceStatus[] = ["compliant", "due-soon", "overdue", "not-applicable"];

type FormState = Partial<ComplianceRegister>;

const emptyForm: FormState = {
  category: "fire-safety",
  name: "",
  frequency: "annual",
  responsibleParty: "",
  nextDue: "",
  status: "compliant",
};

const ComplianceRegisterPage: React.FC = () => {
  const [items, setItems] = useState<ComplianceRegister[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<FormState | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<ComplianceRegister | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      setItems(await fetchComplianceRegister());
    } catch {
      setError("Failed to load compliance register.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startCreate = () => {
    setEditingId(null);
    setEditing({ ...emptyForm });
  };

  const startEdit = (item: ComplianceRegister) => {
    setEditingId(item.id);
    setEditing({ ...item });
  };

  const cancel = () => {
    setEditing(null);
    setEditingId(null);
  };

  const save = async () => {
    if (!editing) return;
    if (!editing.name || !editing.responsibleParty || !editing.nextDue) {
      setError("Name, responsible party and next due date are required.");
      return;
    }
    try {
      setSaving(true);
      if (editingId) {
        await updateComplianceItem(editingId, editing);
      } else {
        await createComplianceItem(editing);
      }
      cancel();
      await load();
    } catch {
      setError("Failed to save compliance item.");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteComplianceItem(confirmDelete.id);
      setConfirmDelete(null);
      await load();
    } catch {
      setError("Failed to delete compliance item.");
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Compliance Register"
        description="Statutory inspections, insurance policies and obligations for this owner."
        actions={
          <button
            type="button"
            onClick={startCreate}
            className="inline-flex items-center px-3 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700"
          >
            + New Item
          </button>
        }
      />

      {error && <ErrorAlert message={error} onRetry={load} />}
      {loading && <Spinner />}

      {!loading && items.length === 0 && !editing && (
        <EmptyState
          title="No compliance items yet"
          description="Add fire risk assessments, insurance policies, gas safety checks and more."
          actionText="+ New Item"
          onAction={startCreate}
        />
      )}

      {editing && (
        <ComplianceForm
          form={editing}
          saving={saving}
          onChange={(patch) => setEditing({ ...editing, ...patch })}
          onCancel={cancel}
          onSave={save}
          mode={editingId ? "edit" : "create"}
        />
      )}

      {!loading && items.length > 0 && (
        <div className="bg-white shadow rounded-lg overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <Th>Name</Th>
                <Th>Category</Th>
                <Th>Frequency</Th>
                <Th>Responsible</Th>
                <Th>Next Due</Th>
                <Th>Status</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((i) => (
                <tr key={i.id}>
                  <Td>{i.name}</Td>
                  <Td className="capitalize">{i.category.replace(/-/g, " ")}</Td>
                  <Td className="capitalize">{i.frequency.replace(/-/g, " ")}</Td>
                  <Td>{i.responsibleParty}</Td>
                  <Td>{i.nextDue}</Td>
                  <Td>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                      {i.status}
                    </span>
                  </Td>
                  <Td>
                    <div className="flex gap-3">
                      <button
                        onClick={() => startEdit(i)}
                        className="text-primary-700 hover:text-primary-900 text-sm font-medium"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => setConfirmDelete(i)}
                        className="text-red-700 hover:text-red-900 text-sm font-medium"
                      >
                        Delete
                      </button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        isOpen={!!confirmDelete}
        title="Delete compliance item"
        message={`Delete "${confirmDelete?.name}"? This cannot be undone.`}
        confirmText="Delete"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
        onClose={() => setConfirmDelete(null)}
        onConfirm={doDelete}
      />
    </div>
  );
};

const ComplianceForm: React.FC<{
  form: FormState;
  saving: boolean;
  mode: "create" | "edit";
  onChange: (patch: Partial<FormState>) => void;
  onCancel: () => void;
  onSave: () => void;
}> = ({ form, saving, mode, onChange, onCancel, onSave }) => (
  <div className="bg-white shadow rounded-lg p-4 space-y-4">
    <h3 className="font-semibold text-gray-900">
      {mode === "create" ? "New compliance item" : "Edit compliance item"}
    </h3>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Field label="Name">
        <input
          type="text"
          value={form.name || ""}
          onChange={(e) => onChange({ name: e.target.value })}
          className="form-input"
        />
      </Field>
      <Field label="Category">
        <select
          value={form.category}
          onChange={(e) => onChange({ category: e.target.value as ComplianceCategory })}
          className="form-input"
        >
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c.replace(/-/g, " ")}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Frequency">
        <select
          value={form.frequency}
          onChange={(e) => onChange({ frequency: e.target.value as ComplianceFrequency })}
          className="form-input"
        >
          {FREQUENCIES.map((f) => (
            <option key={f} value={f}>
              {f.replace(/-/g, " ")}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Status">
        <select
          value={form.status}
          onChange={(e) => onChange({ status: e.target.value as ComplianceStatus })}
          className="form-input"
        >
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Responsible Party">
        <input
          type="text"
          value={form.responsibleParty || ""}
          onChange={(e) => onChange({ responsibleParty: e.target.value })}
          className="form-input"
        />
      </Field>
      <Field label="Next Due">
        <input
          type="date"
          value={form.nextDue || ""}
          onChange={(e) => onChange({ nextDue: e.target.value })}
          className="form-input"
        />
      </Field>
      <Field label="Last Completed" className="md:col-span-1">
        <input
          type="date"
          value={form.lastCompleted || ""}
          onChange={(e) => onChange({ lastCompleted: e.target.value })}
          className="form-input"
        />
      </Field>
      <Field label="Description" className="md:col-span-2">
        <textarea
          rows={2}
          value={form.description || ""}
          onChange={(e) => onChange({ description: e.target.value })}
          className="form-input"
        />
      </Field>
      <Field label="Notes" className="md:col-span-2">
        <textarea
          rows={2}
          value={form.notes || ""}
          onChange={(e) => onChange({ notes: e.target.value })}
          className="form-input"
        />
      </Field>
    </div>
    <div className="flex justify-end gap-2">
      <button
        onClick={onCancel}
        disabled={saving}
        className="px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md"
      >
        Cancel
      </button>
      <button
        onClick={onSave}
        disabled={saving}
        className="px-3 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md disabled:opacity-50"
      >
        {saving ? "Saving..." : "Save"}
      </button>
    </div>
  </div>
);

const Field: React.FC<{ label: string; className?: string; children: React.ReactNode }> = ({
  label,
  className,
  children,
}) => (
  <div className={className}>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    {children}
  </div>
);

const Th: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
    {children}
  </th>
);

const Td: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <td className={`px-4 py-2 text-sm text-gray-900 ${className || ""}`}>{children}</td>
);

export default ComplianceRegisterPage;
